import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./FlagModal.css";

const FLAG_COMMENT_MAX = 280;
const MODAL_MIN_WIDTH = 280;
const VIEWPORT_PAD = 12;
const MODAL_GAP = 8;
const MODAL_EST_HEIGHT = 240;
const TOP_ZONE_RATIO = 0.32;
const BOTTOM_ZONE_RATIO = 0.68;

const resolvePlacement = (anchor, modalHeight) => {
  const viewportHeight = window.innerHeight;
  const top = anchor?.top ?? 0;
  const bottom = anchor?.bottom ?? top;
  const spaceAbove = top - VIEWPORT_PAD;
  const spaceBelow = viewportHeight - bottom - VIEWPORT_PAD;
  const needed = modalHeight + MODAL_GAP;

  const inTopZone = top < viewportHeight * TOP_ZONE_RATIO;
  const inBottomZone = bottom > viewportHeight * BOTTOM_ZONE_RATIO;

  if (inTopZone) return "below";
  if (inBottomZone) return "above";

  if (spaceBelow >= needed && spaceAbove >= needed) {
    return spaceBelow >= spaceAbove ? "below" : "above";
  }
  if (spaceBelow >= needed) return "below";
  if (spaceAbove >= needed) return "above";
  return spaceBelow >= spaceAbove ? "below" : "above";
};

const clampHorizontal = (left, width) => {
  let nextLeft = left ?? VIEWPORT_PAD;
  if (nextLeft + width > window.innerWidth - VIEWPORT_PAD) {
    nextLeft = Math.max(
      VIEWPORT_PAD,
      window.innerWidth - width - VIEWPORT_PAD,
    );
  }
  return Math.max(VIEWPORT_PAD, nextLeft);
};

const FlagModal = ({
  open,
  mode = "add", // "add" | "edit"
  fieldLabel = "",
  initialComment = "",
  anchor = null, // { top, left, width, bottom, height }
  preferredPlacement = null, // "above" | "below" | null
  onClose,
  onSave,
  onRemove,
}) => {
  const [comment, setComment] = useState(initialComment);
  const [position, setPosition] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setComment(initialComment || "");
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, initialComment, onClose]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const modalEl = modalRef.current;
      if (!modalEl || !anchor) return;

      const modalHeight = modalEl.offsetHeight || MODAL_EST_HEIGHT;
      const width = Math.max(anchor.width || MODAL_MIN_WIDTH, MODAL_MIN_WIDTH);
      const left = clampHorizontal(anchor.left, width);

      const placement =
        preferredPlacement || resolvePlacement(anchor, modalHeight);

      let top;
      if (placement === "below") {
        top = (anchor.bottom ?? anchor.top ?? 0) + MODAL_GAP;
        if (top + modalHeight > window.innerHeight - VIEWPORT_PAD) {
          const aboveTop =
            (anchor.top ?? VIEWPORT_PAD) - modalHeight - MODAL_GAP;
          if (aboveTop >= VIEWPORT_PAD) {
            top = aboveTop;
          } else {
            top = Math.max(
              VIEWPORT_PAD,
              window.innerHeight - modalHeight - VIEWPORT_PAD,
            );
          }
        }
      } else {
        top = (anchor.top ?? VIEWPORT_PAD) - modalHeight - MODAL_GAP;
        if (top < VIEWPORT_PAD) {
          const belowTop = (anchor.bottom ?? anchor.top ?? 0) + MODAL_GAP;
          if (belowTop + modalHeight <= window.innerHeight - VIEWPORT_PAD) {
            top = belowTop;
          } else {
            top = VIEWPORT_PAD;
          }
        }
      }

      setPosition({ top, left, width, placement });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchor, preferredPlacement]);

  if (!open) return null;

  const trimmed = comment.trim();
  const canSave = trimmed.length > 0;
  const isEdit = mode === "edit";

  return (
    <div className="rr-flag-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={modalRef}
        className={`rr-flag-modal ${position ? "is-anchored" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? "Edit Flag" : "Add Flag"}
        style={
          position
            ? {
                top: position.top,
                left: position.left,
                width: position.width,
              }
            : undefined
        }
        onClick={(event) => event.stopPropagation()}
      >
        <div className="rr-flag-modal__title">{fieldLabel}</div>
        <div className="rr-flag-modal__field">
          <textarea
            className="rr-flag-modal__textarea"
            value={comment}
            maxLength={FLAG_COMMENT_MAX}
            placeholder="Add flag comment"
            rows={4}
            autoFocus
            onChange={(event) => setComment(event.target.value)}
          />
          <div className="rr-flag-modal__counter">
            {comment.length}/{FLAG_COMMENT_MAX}
          </div>
        </div>
        <div className="rr-flag-modal__actions">
          {isEdit ? (
            <>
              <button
                type="button"
                className="rr-flag-modal__btn rr-flag-modal__btn--primary"
                disabled={!canSave}
                onClick={() => onSave?.(trimmed)}
              >
                Update Flag
              </button>
              <button
                type="button"
                className="rr-flag-modal__btn rr-flag-modal__btn--secondary"
                onClick={() => onRemove?.()}
              >
                Remove
              </button>
            </>
          ) : (
            <button
              type="button"
              className="rr-flag-modal__btn rr-flag-modal__btn--primary"
              disabled={!canSave}
              onClick={() => onSave?.(trimmed)}
            >
              <span
                className="material-symbols-rounded rr-flag-modal__btn-icon"
                aria-hidden="true"
              >
                flag
              </span>
              Add Flag
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlagModal;
export { FLAG_COMMENT_MAX };
